/**
 * Storage Benchmark Certification — read-time enforcement.
 *
 * Persisted `market_comparisons` rows may predate the certified storage engine
 * (or come from an AI provider that compared a per-period charge to a per-day
 * band, or used a passenger market for a commercial vehicle). Every consumer of
 * the claim bundle reads comparisons through one place, so certification is
 * applied there: a storage row either carries a class-matched, unit-normalized
 * benchmark, or it carries no benchmark at all.
 *
 * This never invents a benchmark and never touches non-storage rows.
 */
import type { InvoiceData, MarketComparison } from "@/lib/db/models";
import { classifyVehicle, type VehicleClassification } from "./vehicle-classification";
import {
  selectStorageBenchmark,
  STORAGE_DATASET_VERSION,
  compareStorageLine,
  isStorageDescription,
  normalizeStorageLine,
  type StorageLineInput,
} from "./storage-benchmark";
import { auditBenchmarkFamilies, type BenchmarkFamilyAudit } from "./benchmark-families";

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}
function num(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v.replace(/[$,]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

type RawLine = Record<string, unknown>;

function lineInput(li: RawLine): StorageLineInput {
  return {
    description: str(li.description) ?? str(li.name) ?? str(li.item),
    quantity: num(li.quantity) ?? num(li.qty) ?? num(li.days),
    unitPrice: num(li.unit_price) ?? num(li.unitPrice) ?? num(li.rate),
    total: num(li.total) ?? num(li.amount),
  };
}

function normKey(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

export function classifyClaimVehicle(invoiceData: InvoiceData | null): VehicleClassification {
  const ex = (invoiceData?.extracted ?? {}) as Record<string, unknown>;
  const vehicle = (ex.vehicle ?? {}) as Record<string, unknown>;
  const lines = (invoiceData?.line_items ?? []) as RawLine[];
  return classifyVehicle({
    vehicleDescription: str(ex.vehicle_description) ?? str(vehicle.description) ?? str(ex.vehicleDescription),
    make: str(ex.vehicle_make) ?? str(vehicle.make),
    model: str(ex.vehicle_model) ?? str(vehicle.model),
    bodyType: str(ex.body_type) ?? str(vehicle.body_type),
    vin: str(ex.vin) ?? str(vehicle.vin),
    gvwrLbs: num(ex.gvwr_lbs) ?? num(vehicle.gvwr),
    lineItems: lines.map((li) => ({ description: str(li.description) ?? str(li.name) })),
    rawText: str(ex.raw_text),
    extractionConfidence: num(ex.confidence),
  });
}

/** Signals that a persisted band was priced for a lighter vehicle class. */
const LIGHTER_CLASS_SOURCE = /light[\s-]*duty|passenger|motorcycle|sedan/i;

const HEAVY_CLASSES = new Set([
  "heavy_duty_commercial",
  "semi_tractor",
  "bus",
  "construction_equipment",
  "agricultural_equipment",
]);

/**
 * A persisted benchmark is only overridden when it is affirmatively wrong for
 * the classified vehicle. A statutory cap or a local band is more specific than
 * the curated national baseline, so a compatible persisted band is preserved —
 * certification never deletes a valid benchmark, it only corrects a wrong one.
 */
function persistedBandIsWrongClass(
  m: MarketComparison,
  classification: VehicleClassification,
  certifiedPerDayLow: number,
): boolean {
  if (!HEAVY_CLASSES.has(classification.vehicleClass)) return false;
  if (LIGHTER_CLASS_SOURCE.test(m.source ?? "")) return true;
  const median = m.market_median ?? m.local_average ?? null;
  // A heavy vehicle priced far under the heavy-duty floor is a light-duty band.
  return typeof median === "number" && median > 0 && median < certifiedPerDayLow * 0.75;
}

export function certifyStorageComparisons(
  comparisons: MarketComparison[],
  invoiceData: InvoiceData | null,
): MarketComparison[] {
  if (!comparisons.length) return comparisons;
  const storageRows = comparisons.filter((m) =>
    isStorageDescription(m.line_item ?? m.service_type),
  );
  if (!storageRows.length) return comparisons;

  const classification = classifyClaimVehicle(invoiceData);
  const lines = (invoiceData?.line_items ?? []) as RawLine[];
  const storageLines = lines
    .map(lineInput)
    .filter((li) => isStorageDescription(li.description));

  // A storage line that carries an explicit date range establishes the storage
  // period for the whole claim; sibling storage rows whose description lost the
  // dates inherit it rather than being left unbenchmarked.
  const claimStorageDays =
    storageLines
      .map((li) => normalizeStorageLine(li).durationDays)
      .find((d): d is number => typeof d === "number" && d > 0) ?? null;

  const zip = comparisons.find((m) => m.zip || m.zip_code)?.zip ?? null;
  const state = comparisons.find((m) => m.state)?.state ?? null;

  return comparisons.map((m) => {
    const description = m.line_item ?? m.service_type ?? "";
    if (!isStorageDescription(description)) return m;

    // Vehicle class unknown → no certified market exists, so the persisted
    // benchmark stands exactly as recorded. Never blank a benchmark here.
    if (classification.vehicleClass === "unknown") return m;

    const key = normKey(description);
    const candidates = storageLines.filter((li) => {
      const k = normKey(li.description ?? "");
      return k === key || (k.length > 3 && (k.includes(key) || key.includes(k)));
    });
    // OCR often emits the same storage charge twice: once carrying the date
    // range and once without it. Prefer the candidate that still carries its
    // own dates, so duration and billing unit come from the strongest evidence.
    const dated = candidates.find(
      (li) => normalizeStorageLine(li).durationBasis === "line_item_date_range",
    );
    const firstMatch = candidates[0] ?? null;
    // When the matched line lost its dates, adopt the identically-billed dated
    // sibling elsewhere on the invoice — same charge, stronger evidence.
    const datedSibling =
      !dated && firstMatch
        ? storageLines.find((li) => {
            const n = normalizeStorageLine(li);
            return (
              n.durationBasis === "line_item_date_range" &&
              n.billedAmount !== null &&
              n.billedAmount === normalizeStorageLine(firstMatch).billedAmount
            );
          })
        : undefined;
    const matched = dated ?? datedSibling ?? firstMatch;

    const billed = m.invoice_amount ?? m.invoice_rate ?? null;
    // The claim-level storage period always backstops a matched line whose own
    // description lost its date range (OCR frequently splits it onto a
    // duplicate line), so a per-period charge is never left un-normalized.
    const line: StorageLineInput = {
      ...(matched ?? { description, total: billed }),
      extractedStorageDays: matched?.extractedStorageDays ?? claimStorageDays,
    };

    const cmp = compareStorageLine({
      classification,
      line: { ...line, description: line.description ?? description },
      options: { zip, state },
    });

    if (!cmp.benchmark) return m;

    const days = cmp.line.durationDays ?? claimStorageDays;
    const wrongClass = persistedBandIsWrongClass(m, classification, cmp.benchmark.perDay.low);
    const hasPersistedBand =
      typeof (m.market_median ?? m.local_average) === "number" &&
      typeof m.market_low === "number" &&
      typeof m.market_high === "number";

    // Which per-day band governs: the persisted one when it is class-compatible
    // (it is more local/statutory), otherwise the certified curated band.
    const perDayBand =
      hasPersistedBand && !wrongClass
        ? {
            low: m.market_low as number,
            median: (m.market_median ?? m.local_average) as number,
            high: m.market_high as number,
          }
        : cmp.benchmark.perDay;

    // Scale to the billing unit actually used on the invoice.
    const perPeriod = cmp.line.billingUnit === "per_period" && typeof days === "number" && days > 0;
    if (cmp.line.billingUnit === "unknown" && hasPersistedBand && !wrongClass) return m;

    const band = perPeriod
      ? {
          low: round2(perDayBand.low * days!),
          median: round2(perDayBand.median * days!),
          high: round2(perDayBand.high * days!),
        }
      : perDayBand;

    const periodCharge = cmp.line.billedAmount ?? billed;
    if (typeof periodCharge !== "number") return m;

    const difference = round2(periodCharge - band.median);
    const variancePercent =
      band.median > 0 ? Math.round(((periodCharge - band.median) / band.median) * 1000) / 10 : null;
    const assessment =
      periodCharge > band.high
        ? "above_market"
        : periodCharge < band.low
          ? "below_market"
          : "within_market";

    const sourceLabel =
      hasPersistedBand && !wrongClass
        ? (m.source ?? `${cmp.benchmark.benchmarkLabel} market`)
        : `${cmp.benchmark.benchmarkLabel} market${cmp.benchmark.zip ? ` · ZIP ${cmp.benchmark.zip}` : cmp.benchmark.state ? ` · ${cmp.benchmark.state}` : ""}`;

    return {
      ...m,
      invoice_amount: periodCharge,
      invoice_rate: cmp.line.perDayAmount ?? m.invoice_rate,
      market_low: band.low,
      market_median: band.median,
      market_high: band.high,
      local_average: band.median,
      typical_low: band.low,
      typical_high: band.high,
      difference_amount: difference,
      difference_percent: variancePercent,
      assessment,
      confidence: hasPersistedBand && !wrongClass ? m.confidence : cmp.benchmark.providerConfidence,
      source: sourceLabel,
    };
  });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/* ───────────── benchmark family certification (internal, Section 10) ───────────── */

/**
 * Internal read-time rule set: every persisted comparison must belong to a
 * benchmark family that is compatible with the classified vehicle, and must
 * record its market region. Returns an audit only — it never alters a value
 * and is never rendered to the customer.
 */
export function auditClaimBenchmarkFamilies(
  comparisons: MarketComparison[],
  invoiceData: InvoiceData | null,
): BenchmarkFamilyAudit & { region: string | null; datasetVersion: string } {
  const classification = classifyClaimVehicle(invoiceData);
  const storageBenchmark = selectStorageBenchmark(classification, {
    zip: comparisons.find((m) => m.zip_code ?? m.zip)?.zip_code ?? null,
    state: comparisons.find((m) => m.state)?.state ?? null,
  });
  const audit = auditBenchmarkFamilies({
    vehicleClass: classification.vehicleClass,
    storageMarketLabel: storageBenchmark?.benchmarkLabel ?? null,
    charges: comparisons.map((m) => ({
      description: m.line_item ?? m.service_type,
      serviceType: m.service_type,
    })),
  });
  const region =
    comparisons.find((m) => m.zip_code ?? m.zip)?.zip_code ??
    comparisons.find((m) => m.state)?.state ??
    null;
  return { ...audit, region, datasetVersion: STORAGE_DATASET_VERSION };
}
